import {
  CANONICAL_PROJECT_ROOM_TABS,
  resolveProjectRoomTab,
  type CanonicalProjectRoomTab,
} from "@enrein/shared-contracts";

export const GREENFIELD_NAV_ITEMS = ["today", "businesses", "work", "settings"] as const;
export type GreenfieldNavId = (typeof GREENFIELD_NAV_ITEMS)[number];

export const GREENFIELD_VIEWS = [
  "today",
  "businesses",
  "business",
  "portfolio",
  "work",
  "create",
  "project",
  "workbench",
  "settings",
] as const;
export type GreenfieldView = (typeof GREENFIELD_VIEWS)[number];

export const GREENFIELD_NAV_LABELS: Record<GreenfieldNavId, string> = {
  today: "Today",
  businesses: "Businesses",
  work: "Work",
  settings: "Settings",
};

export interface GreenfieldRouteState {
  readonly view: GreenfieldView;
  readonly projectId: string | null;
  readonly businessId: string | null;
  readonly section: CanonicalProjectRoomTab | null;
}

export function isGreenfieldView(value: string): value is GreenfieldView {
  return (GREENFIELD_VIEWS as readonly string[]).includes(value);
}

export function parseGreenfieldRoute(search: string): GreenfieldRouteState {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const viewRaw = params.get("view") ?? "today";
  const view = isGreenfieldView(viewRaw) ? viewRaw : "today";
  const sectionRaw = params.get("section");
  return {
    view,
    projectId: params.get("project")?.trim() || null,
    businessId: params.get("business")?.trim() || null,
    section: sectionRaw
      ? (resolveProjectRoomTab(sectionRaw) as CanonicalProjectRoomTab)
      : view === "project"
        ? "overview"
        : null,
  };
}

export function buildGreenfieldSearch(state: GreenfieldRouteState): string {
  const params = new URLSearchParams();
  if (state.view !== "today") params.set("view", state.view);
  if (state.projectId) params.set("project", state.projectId);
  if (state.businessId) params.set("business", state.businessId);
  if (state.section && state.view === "project") params.set("section", state.section);
  const q = params.toString();
  return q.length > 0 ? `?${q}` : "";
}

export function navIdForView(view: GreenfieldView): GreenfieldNavId {
  if (view === "settings") return "settings";
  if (view === "work" || view === "create" || view === "project" || view === "workbench") return "work";
  if (view === "businesses" || view === "business" || view === "portfolio") return "businesses";
  return "today";
}

export function projectRoomTabs(): readonly CanonicalProjectRoomTab[] {
  return CANONICAL_PROJECT_ROOM_TABS;
}

export function projectRoomTabLabel(tab: CanonicalProjectRoomTab): string {
  if (tab === "approval-delivery") return "Approval & Delivery";
  return tab.replace(/-/g, " ");
}
